import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

const createAppTheme = (isDarkMode) => {
  return createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#222222',
      },
      secondary: {
        main: '#FF4C29',
      },
      customGreen: {
        main: '#00B074',
        contrastText: '#fff',
      },
      customBlue: {
        main: '#2D87F0',
        contrastText: '#fff',
      },
      background: {
        default: isDarkMode ? '#0a0a0a' : '#ffffff',
        paper: isDarkMode ? '#1a1a1a' : '#ffffff',
      },
      text: {
        primary: isDarkMode ? '#ffffff' : '#000000',
        secondary: isDarkMode ? '#b3b3b3' : '#666666',
      },
    },
    components: {
      // Ensure Material-UI components respect dark mode
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
              '& fieldset': {
                borderColor: isDarkMode ? '#404040' : '#e0e0e0',
              },
              '&:hover fieldset': {
                borderColor: isDarkMode ? '#606060' : '#b0b0b0',
              },
              '&.Mui-focused fieldset': {
                borderColor: isDarkMode ? '#90caf9' : '#1976d2',
              },
            },
            '& .MuiInputLabel-root': {
              color: isDarkMode ? '#b3b3b3' : '#666666',
            },
            '& .MuiInputBase-input': {
              color: isDarkMode ? '#ffffff' : '#000000',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
            color: isDarkMode ? '#ffffff' : '#000000',
          },
        },
      },
      MuiBox: {
        styleOverrides: {
          root: {
            '&[data-theme-box="true"]': {
              backgroundColor: isDarkMode ? '#2a2a2a' : '#f6f6f6',
            },
          },
        },
      },
    },
  });
};

export const ThemeContextProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage?.getItem('darkMode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    // Default to system preference (fallback to false in test environment)
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => {
      const newMode = !prev;
      if (typeof window !== 'undefined' && localStorage) {
        localStorage.setItem('darkMode', JSON.stringify(newMode));
      }
      return newMode;
    });
  };

  const theme = createAppTheme(isDarkMode);

  useEffect(() => {
    // Listen for system preference changes (only in browser)
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        // Only update if no manual preference is saved
        if (localStorage?.getItem('darkMode') === null) {
          setIsDarkMode(e.matches);
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  const value = {
    isDarkMode,
    toggleDarkMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};